import { defaultSafecareConfig } from './policyConfig.js';
import { CONSERVATIVE_FACTOR, compoundingEffectsConfig } from '../config/compoundingEffects.js';
import { getEffectiveAdminOverheadRate, getEffectiveDrugCeilingPricePctOfUS, getDrugCeilingInputs } from './policyMath.js';

function isPlainObject(x) {
    return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function deepEqual(a, b) {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
    if (isPlainObject(a) && isPlainObject(b)) return JSON.stringify(a) === JSON.stringify(b);
    return false;
}

function clamp01(x) {
    if (Number.isNaN(x)) return 0;
    return Math.max(0, Math.min(1, x));
}

export const PARAM_SOURCE = {
    BILL: 'bill',
    MODEL: 'model_assumption',
    DERIVED: 'derived',
    LEGACY: 'legacy'
};

/**
 * Parameter Registry
 * Central source-of-truth for:
 * - meaning (what the parameter does)
 * - units (how to interpret the value)
 * - provenance (bill vs model assumption)
 * - evidence notes (why the default/range exists)
 *
 * NOTE: Not every config key is registered yet; unregistered keys are displayed
 * as "internal/unregistered" so we can audit and continuously improve coverage.
 */
export const PARAMETER_REGISTRY = [
    // ===========================
    // Financing (Statute)
    // ===========================
    {
        key: 'employerRate',
        label: 'Employer payroll contribution rate',
        group: 'Financing (Sec 202)',
        unit: 'percent',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 202(b)',
            excerpt: '“The standard contribution rate shall be 8.0 percent of wages described in subsection (a).”'
        },
        description: 'Payroll contribution assessed on covered wages.',
    },
    {
        key: 'baseIndividualRate',
        label: 'Individual base contribution rate',
        group: 'Financing (Sec 203)',
        unit: 'percent',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 203(c)(1)',
            excerpt: '“... the base individual health contribution rate shall be 3.5 percent of Modified Adjusted Gross Income, applied from the first dollar...”'
        },
        description: 'Base income contribution assessed on covered income (AGI proxy).',
    },
    {
        key: 'baseSelfEmployedRate',
        label: 'Self-employed base contribution rate',
        group: 'Financing (Sec 202+203)',
        unit: 'percent',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 203(c)(2)',
            excerpt: '“... the base rate shall be equal to the sum of the National Health Employer Contribution base rate ... and the base individual rate ...”'
        },
        description: 'Combined employer+individual style rate applied to self-employment base.',
    },
    {
        key: 'surtaxThresholds',
        label: 'Surtax thresholds (tier cutoffs)',
        group: 'Financing (Sec 203(c)(3))',
        unit: 'usd',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 203(c)(3)(A)-(C)',
            excerpt: '“... 1.0 percent ... above 100,000 dollars ... additional 1.5 percent ... above 250,000 dollars ... additional 2.0 percent ... above 500,000 dollars.”'
        },
        description: 'Income thresholds where surtax tiers apply.',
    },
    {
        key: 'surtaxRates',
        label: 'Surtax rates (tier add-ons)',
        group: 'Financing (Sec 203(c)(3))',
        unit: 'percent',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 203(c)(3)(A)-(C)',
            excerpt: '“... 1.0 percent ... additional 1.5 percent ... additional 2.0 percent ...”'
        },
        description: 'Marginal surtax rates applied above corresponding thresholds.',
    },
    {
        key: 'startYear',
        label: 'Simulation start year (implementation year)',
        group: 'Timing (model)',
        unit: 'raw',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Simulation frame', evidence: ['Defines first modeled implementation year for cohort ramp + stabilizer logic.'] },
        description: 'First year the SAFECARE system is modeled as active in the simulation.',
    },
    {
        key: 'simulationYears',
        label: 'Displayed simulation duration',
        group: 'Timing (UI)',
        unit: 'raw',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'UI setting' },
        description: 'Number of years shown in charts/tables (the engine may compute more years internally).',
    },

    // ===========================
    // Cost-sharing (Statute)
    // ===========================
    {
        key: 'annualCopaymentCap',
        label: 'Annual copayment cap (per member)',
        group: 'Cost-sharing (Sec 204)',
        unit: 'usd',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 204(d)(2)',
            excerpt: '“The initial cap shall be 750 dollars per person per year in 2026 dollars.”'
        },
        description: 'Maximum annual out-of-pocket copayments per member in the essential benefit floor.',
    },
    {
        key: 'avgCopay',
        label: 'Average copay per visit (model proxy)',
        group: 'Cost-sharing (Sec 204)',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Modeled proxy', evidence: ['Used to approximate copay receipts under the annual cap.'] },
        description: 'Average per-encounter copay used for aggregate copay-revenue approximation.',
    },

    // ===========================
    // Revenue realism (Model)
    // ===========================
    {
        key: 'taxEvasionRate',
        label: 'Tax evasion / collection gap',
        group: 'Revenue realism',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'IRS tax gap (directional)', evidence: ['Represents aggregate shortfall vs theoretical collections.'] },
        description: 'Fraction of modeled tax base that fails to be collected.',
    },
    {
        key: 'surtaxAvoidance',
        label: 'Surtax avoidance fraction',
        group: 'Revenue realism',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'High-income behavioral response', evidence: ['Represents income shifting/avoidance that weakens surtax yield.'] },
        description: 'Fraction of surtax-eligible income that avoids effective surtax collection.',
    },
    {
        key: 'payrollBaseErosion',
        label: 'Payroll base erosion (annual)',
        group: 'Revenue realism',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Labor-market structure sensitivity', evidence: ['Models shifting income from W-2 wages to non-wage forms over time.'] },
        description: 'Annual compounding erosion of the wage base available for payroll contributions.',
    },

    // ===========================
    // Trust Fund stabilization (Statute + stress tests)
    // ===========================
    {
        key: 'reserveTargetMin',
        label: 'Reserve ratio lower bound',
        group: 'Trust Fund (Sec 206)',
        unit: 'percent',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 206(b)',
            excerpt: '“... maintain a reserve ratio within a target band of not less than 5 percent and not more than 10 percent.”'
        },
        description: 'If reserve ratio falls below this band, automatic upward adjustment may trigger.',
    },
    {
        key: 'reserveTargetMax',
        label: 'Reserve ratio upper bound',
        group: 'Trust Fund (Sec 206)',
        unit: 'percent',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 206(b)',
            excerpt: '“... maintain a reserve ratio within a target band of not less than 5 percent and not more than 10 percent.”'
        },
        description: 'If reserve ratio rises above this band, automatic downward adjustment may trigger.',
    },
    {
        key: 'enforceTrustFundRules',
        label: 'Enforce automatic stabilizer mechanics',
        group: 'Trust Fund (Sec 206) – enforcement realism',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Political-economy stress test', evidence: ['Represents non-enforcement / override risk even if statute allows adjustments.'] },
        description: 'How fully Sec 206 automatic adjustments are carried out (1 full, 0 none).',
    },
    {
        key: 'allowAutoIncrease',
        label: 'Allow automatic rate increases',
        group: 'Trust Fund (Sec 206) – political override',
        unit: 'boolean',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 206(h) override + real-world risk', evidence: ['Models Congress blocking upward steps during deficits.'] },
        description: 'If false, upward adjustments are suppressed even when triggers occur.',
    },
    {
        key: 'allowAutoDecrease',
        label: 'Allow automatic rate decreases',
        group: 'Trust Fund (Sec 206) – political override',
        unit: 'boolean',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 206(h) override + real-world risk', evidence: ['Models Congress blocking downward steps during surplus.'] },
        description: 'If false, downward adjustments are suppressed even when triggers occur.',
    },
    {
        key: 'initialTrustFundSeed',
        label: 'Initial trust fund seed (one-time)',
        group: 'Trust Fund (model input)',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Scenario knob', evidence: ['Allows stress testing transitional liquidity and ramp timing.'] },
        description: 'Optional one-time starting balance injected into the trust fund at startYear.',
    },

    // ===========================
    // Provider payments & infrastructure (Statute + model knobs)
    // ===========================
    {
        key: 'providerPaymentFloor',
        label: 'Provider payment floor (vs Medicare)',
        group: 'Providers (payments)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Payment adequacy stress test', evidence: ['Used to stress-test political/provider pressure for above-Medicare payments.'] },
        description: 'Multiplier applied to modeled Medicare-based provider rates as a floor.',
    },
    {
        key: 'ruralUplift',
        label: 'Rural uplift multiplier',
        group: 'Providers (payments)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Access protection stress test' },
        description: 'Additional multiplier for rural/certain facilities to protect access.',
    },
    {
        key: 'capitalGrantsAnnual',
        label: 'Annual capital grants budget',
        group: 'Providers (capital)',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 604 appropriations (modeled)', evidence: ['Budget used as an aggregate proxy for capital investment support.'] },
        description: 'Annual budget for capital grants / infrastructure support.',
    },
    {
        key: 'debtRedemptionBudget',
        label: 'Annual medical debt redemption budget',
        group: 'Households / debt',
        unit: 'usd',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 701(b)(1)',
            excerpt: '“... not less than 5,000,000,000 dollars and not more than 15,000,000,000 dollars per year ...”',
            evidence: ['Config selects a point within the statutory authorized range.']
        },
        description: 'Annual budget for the medical debt redemption facility (modeled aggregate).',
    },

    // ===========================
    // Provider payment base (Sec 402, modeled as multipliers)
    // ===========================
    {
        key: 'providerPaymentMultiplier',
        label: 'Base provider payment multiplier (vs Medicare)',
        group: 'Providers (Sec 402 – base)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.BILL, citation: 'Sec. 402 (rate schedule)', evidence: ['Modeled as Medicare-multiple applied to baseline provider costs.'] },
        description: 'Base multiplier applied to Medicare-based provider unit costs (before transitions/floors).',
    },
    {
        key: 'ruralHospitalProtection',
        label: 'Rural / critical access protection multiplier',
        group: 'Providers (Sec 402 – rural)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.BILL, citation: 'Sec. 402 (rural protections)', evidence: ['Modeled as an uplift multiplier.'] },
        description: 'Uplift factor for rural/critical access facilities to protect access during/after transition.',
    },
    {
        key: 'ruralSafetyNetUplift',
        label: 'Safety-net uplift (DSH-like proxy)',
        group: 'Providers (Sec 402 – safety net)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Safety-net proxy', evidence: ['Simplified aggregate uplift for disproportionate-share burden; detailed DSH logic not modeled.'] },
        description: 'Additional uplift applied to represent safety-net burden relief.',
    },

    // ===========================
    // Enforcement toggles (implementation realism)
    // ===========================
    {
        key: 'enforceTuitionCaps',
        label: 'Enforce tuition caps (Title V)',
        group: 'Enforcement realism',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 501 enforcement realism' },
        description: 'How fully tuition caps are implemented/maintained (0 none → 1 full).',
    },
    {
        key: 'enforceDebtRedemption',
        label: 'Enforce debt redemption program (Title VII)',
        group: 'Enforcement realism',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Title VII enforcement realism' },
        description: 'How fully the debt redemption program is executed (0 none → 1 full).',
    },
    {
        key: 'enforceReceivershipProtections',
        label: 'Enforce receivership / continuity protections',
        group: 'Enforcement realism',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Receivership authority realism' },
        description: 'How fully continuity protections are used when entities fail compliance or access is threatened.',
    },
    {
        key: 'enforceMalpracticeReform',
        label: 'Enforce malpractice reform',
        group: 'Enforcement realism',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Malpractice reform realism' },
        description: 'How fully malpractice reform provisions (if any) are implemented in practice (0 none → 1 full).',
    },

    // ===========================
    // Appropriations & program budgets (bill ranges + model controls)
    // ===========================
    {
        key: 'smallInnovatorBudget',
        label: 'Small innovator / transition support budget',
        group: 'Spending programs',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Program budget proxy' },
        description: 'Budget proxy for targeted innovation/transition support programs.',
    },
    {
        key: 'tuitionCapAnnual',
        label: 'Medical degree tuition cap (annual)',
        group: 'Workforce (Title V)',
        unit: 'usd',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 501(b)(1)',
            excerpt: '“... for medical degree programs ... a cap of not more than 15,000 dollars per academic year in 2026 dollars ...”'
        },
        description: 'Maximum annual tuition for covered medical degree programs (indexing handled outside this config).',
    },

    // ===========================
    // General fund reliance policy (Sec 207)
    // ===========================
    {
        key: 'generalFundPhaseDown',
        label: 'Apply general fund transfer phase-down',
        group: 'Trust Fund (Sec 207)',
        unit: 'boolean',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 207 phase-down mechanism' },
        description: 'If true, general fund reliance is reduced along a transition schedule (when modeled).',
    },
    {
        key: 'generalFundPolicy',
        label: 'General fund transfer policy mode',
        group: 'Trust Fund (Sec 207)',
        unit: 'enum',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Policy mode selector' },
        description: "How general fund transfers are treated in the simulation (e.g., 'as_written', 'none', 'phase-down').",
    },

    // ===========================
    // External shocks & macro sensitivities (Model)
    // ===========================
    {
        key: 'drugInnovationReduction',
        label: 'Drug innovation reduction (long-run)',
        group: 'Shocks / risks',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Launch delay / innovation sensitivity' },
        description: 'Long-run reduction in new drug entry (stress test of dynamic innovation response).',
    },
    {
        key: 'medicalTourismLeakage',
        label: 'Medical tourism leakage',
        group: 'Shocks / risks',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Leakage sensitivity' },
        description: 'Fractional leakage of high-income care consumption outside the system (proxy).',
    },
    {
        key: 'wageGrowthDifferential',
        label: 'Wage growth differential (premium relief → wages)',
        group: 'Macroeconomy (model)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Incidence assumption' },
        description: 'Additional wage growth attributed to reduced employer premium burden (incidence proxy).',
    },

    // ===========================
    // Legacy compatibility aliases (prefer surtaxThresholds/surtaxRates)
    // ===========================
    {
        key: 'surtaxBracket1',
        label: 'Surtax bracket 1 (legacy alias)',
        group: 'Financing (legacy)',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility alias' },
        description: 'Legacy alias for surtax thresholds; prefer surtaxThresholds.',
    },
    {
        key: 'surtaxBracket2',
        label: 'Surtax bracket 2 (legacy alias)',
        group: 'Financing (legacy)',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility alias' },
        description: 'Legacy alias for surtax thresholds; prefer surtaxThresholds.',
    },
    {
        key: 'surtaxBracket3',
        label: 'Surtax bracket 3 (legacy alias)',
        group: 'Financing (legacy)',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility alias' },
        description: 'Legacy alias for surtax thresholds; prefer surtaxThresholds.',
    },
    {
        key: 'surtaxRate1',
        label: 'Surtax rate 1 (legacy alias)',
        group: 'Financing (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility alias' },
        description: 'Legacy alias for surtax rates; prefer surtaxRates.',
    },
    {
        key: 'surtaxRate2',
        label: 'Surtax rate 2 (legacy alias)',
        group: 'Financing (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility alias' },
        description: 'Legacy alias for surtax rates; prefer surtaxRates.',
    },
    {
        key: 'surtaxRate3',
        label: 'Surtax rate 3 (legacy alias)',
        group: 'Financing (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility alias' },
        description: 'Legacy alias for surtax rates; prefer surtaxRates.',
    },

    // ===========================
    // Tax base coverage / compliance (Model)
    // ===========================
    {
        key: 'wageCoverage',
        label: 'Wage base coverage (legacy)',
        group: 'Revenue realism (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility with older calculators' },
        description: 'Legacy overall wage coverage/compliance knob (largely superseded by taxEvasionRate).',
    },
    {
        key: 'selfEmploymentCoverage',
        label: 'Self-employment base coverage (legacy)',
        group: 'Revenue realism (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility with older calculators' },
        description: 'Legacy self-employment compliance knob.',
    },
    {
        key: 'passThroughCoverage',
        label: 'Pass-through income coverage (legacy)',
        group: 'Revenue realism (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility with older calculators' },
        description: 'Legacy pass-through income coverage knob.',
    },
    {
        key: 'capitalCoverage',
        label: 'Capital income coverage',
        group: 'Revenue realism',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Out-of-scope base (bill does not rely on broad capital base by default)' },
        description: 'Fraction of capital income treated as contributing base (used only in scenarios).',
    },
    {
        key: 'retirementExemptionLimit',
        label: 'Retirement exemption limit (MAGI reduction cap)',
        group: 'Financing (Sec 203) – base definition',
        unit: 'usd',
        source: {
            kind: PARAM_SOURCE.BILL,
            citation: 'Sec. 203(b)(2)(A)',
            excerpt: '“The Retirement Exemption Limit shall be $100,000.”'
        },
        description: 'Maximum retirement distributions excluded from MAGI for contribution purposes (indexing not modeled here).',
    },
    {
        key: 'nonWageCap',
        label: 'Non-wage income cap',
        group: 'Revenue realism',
        unit: 'usd',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Scenario control' },
        description: 'Optional cap on non-wage income base (scenario control).',
    },

    // ===========================
    // System efficiency proxies (Model)
    // ===========================
    {
        key: 'stateCooperation',
        label: 'State cooperation / data integration',
        group: 'Implementation risk',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Federal backstop + state coordination sensitivity' },
        description: 'Fractional cooperation of states in coordination/data sharing (affects timing/friction proxies).',
    },
    {
        key: 'aiFraudIntensity',
        label: 'Anti-fraud intensity',
        group: 'Administration (model)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Fraud control sensitivity' },
        description: 'Effectiveness intensity of anti-fraud systems (aggregate proxy).',
    },
    {
        key: 'adminWasteReductionMax',
        label: 'Maximum admin waste reduction achievable',
        group: 'Administration (model)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Savings ceiling proxy' },
        description: 'Upper bound on administrative waste savings that can be realized.',
    },
    {
        key: 'drugPriceReductionMax',
        label: 'Maximum drug price reduction achievable (legacy)',
        group: 'Drugs (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility with older calculator' },
        description: 'Legacy max savings knob; current V2 uses price-as-% anchors and enforcement.',
    },
    {
        key: 'tuitionReductionMax',
        label: 'Maximum tuition reduction achievable (legacy)',
        group: 'Workforce (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility with older calculator' },
        description: 'Legacy max savings knob for tuition; current model uses tuition caps + support coverage.',
    },
    {
        key: 'malpracticeReductionMax',
        label: 'Maximum malpractice cost reduction achievable (legacy)',
        group: 'Malpractice (legacy)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.LEGACY, citation: 'Compatibility with older calculator' },
        description: 'Legacy max savings knob; malpractice impacts are modeled elsewhere.',
    },

    // ===========================
    // Behavioral responses & leakage (Model)
    // ===========================
    {
        key: 'supplementalCoverageRate',
        label: 'Supplemental coverage take-up rate',
        group: 'Behavior (model)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Supplemental market sensitivity' },
        description: 'Share of members purchasing supplemental coverage (affects multipayer admin/coordination proxies).',
    },
    {
        key: 'providerPaymentStringency',
        label: 'Provider payment stringency / leakage',
        group: 'Providers (behavioral)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Leakage sensitivity' },
        description: 'How strictly the modeled rate schedule holds in practice (1.0 = no leakage).',
    },

    // ===========================
    // Integration timing (Model)
    // ===========================
    {
        key: 'initialIntegrationShare',
        label: 'Initial integration share',
        group: 'Implementation timing',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Program integration timing proxy' },
        description: 'Initial share of federal program integration achieved early in the implementation ramp.',
    },
    {
        key: 'integrationRampYears',
        label: 'Integration ramp duration',
        group: 'Implementation timing',
        unit: 'years',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Program integration timing proxy' },
        description: 'Years required to reach full integration in the model.',
    },

    // ===========================
    // Workforce transition (Model)
    // ===========================
    {
        key: 'severanceMonths',
        label: 'Administrative workforce severance duration',
        group: 'Workforce transition',
        unit: 'raw',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Transition support assumption' },
        description: 'Severance months for displaced administrative roles (aggregate proxy).',
    },
    {
        key: 'retrainingFundingLevel',
        label: 'Retraining funding level',
        group: 'Workforce transition',
        unit: 'enum',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Transition support assumption' },
        description: "Qualitative setting for retraining investment (e.g., 'low'/'medium'/'high').",
    },
    {
        key: 'tuitionSupportCoverage',
        label: 'Tuition support coverage',
        group: 'Workforce (Title V) – implementation',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 501A execution intensity' },
        description: 'Fraction of eligible tuition support costs actually funded/covered in the model.',
    },
    {
        key: 'serviceObligationStrength',
        label: 'Service obligation strength',
        group: 'Workforce (Title V) – implementation',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Service scholarship enforcement proxy' },
        description: 'Strength of service obligation enforcement for supported trainees (aggregate proxy).',
    },
    {
        key: 'tuitionCapCoverage',
        label: 'Tuition cap coverage (scope of compliance)',
        group: 'Workforce (Title V) – implementation',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Institution compliance coverage' },
        description: 'Share of the training pipeline effectively subject to tuition caps in practice.',
    },
    {
        key: 'reciprocalApprovalSpeed',
        label: 'Reciprocal approval speed',
        group: 'Workforce transition',
        unit: 'enum',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Licensure/credentialing timing proxy' },
        description: "Qualitative speed setting for reciprocal approval/credentialing (e.g., 'expedited').",
    },

    // ===========================
    // Macro/behavioral risk knobs (Model)
    // ===========================
    {
        key: 'macroFeedbackSensitivity',
        label: 'Macro feedback sensitivity',
        group: 'Macroeconomy (model)',
        unit: 'raw',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Macro sensitivity knob' },
        description: 'Scales macro feedback strength (higher = stronger GDP/wage response).',
    },
    {
        key: 'debtReductionReflow',
        label: 'Debt paydown reflow (share of health savings applied to debt)',
        group: 'Federal debt (model realism)',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Political-economy: Congress rarely dedicates 100% of savings to debt paydown' },
        description: 'Share of federal health savings that actually reduces debt (default 25%); remainder assumed spent elsewhere.',
    },
    {
        key: 'providerConsolidationSpeed',
        label: 'Provider consolidation speed (annual)',
        group: 'Providers (behavioral)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Market-structure sensitivity' },
        description: 'Annual consolidation pressure affecting prices/utilization via market power.',
    },
    {
        key: 'legalChallengeDelay',
        label: 'Legal challenge / injunction delay',
        group: 'Implementation risk',
        unit: 'raw',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Litigation timing sensitivity' },
        description: 'Years of implementation delay due to lawsuits/court orders.',
    },
    {
        key: 'ruralHospitalClosureRate',
        label: 'Rural hospital closure rate (per 5 years)',
        group: 'Implementation risk',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Access risk sensitivity' },
        description: 'Closure rate used to model crisis-management costs and access degradation.',
    },
    {
        key: 'providerQualityDecline',
        label: 'Quality decline / complication increase',
        group: 'Implementation risk',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Capacity/quality stress test' },
        description: 'Models increased complications/costs due to staffing strain or implementation failures.',
    },

    // ===========================
    // Administration (bill intent + model realism)
    // ===========================
    {
        key: 'enforceAdminSimplification',
        label: 'Enforce administrative simplification',
        group: 'Administration (Sec 404 + load cap)',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 404 + administrative load cap', evidence: ['Models implementation strength of uniform standards/platform and plan admin caps.'] },
        description: 'How fully the admin burden reductions and uniform standards are realized.',
    },
    {
        key: 'adminEfficiencyFloor',
        label: 'Administrative efficiency floor (multipayer realism)',
        group: 'Administration (real-world stress test)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'International multipayer admin evidence', evidence: ['Used to prevent unrealistically low admin rates in a multipayer clearinghouse design.'] },
        description: 'Minimum achievable admin overhead rate regardless of the transition schedule.',
    },

    // ===========================
    // Drug pricing (Statute + legal/political regimes)
    // ===========================
    {
        key: 'drugPricingMode',
        label: 'Drug pricing legal regime',
        group: 'Drugs (Sec 405)',
        unit: 'enum',
        source: {
            kind: PARAM_SOURCE.MODEL,
            citation: 'Sec. 405(b)(1) + Sec. 405(g)(2)',
            excerpt: '“... shall not exceed the lower of: (A) The Domestic Value Benchmark ...; or (B) 110 percent of the International Reference Price (IRP) ...”\n\n“... If the application of the International Reference Price ... is held to be invalid ... the maximum reimbursement rate ... shall be determined solely by reference to the Domestic Value Benchmark ...”',
            evidence: ['Encodes court outcomes and statutory fallback structure: min(IRP, DVB) vs DVB-only when IRP invalidated.']
        },
        description: "Selects which ceiling binds: 'bill' min(IRP,DVB), 'irp_only', or 'dvb_only'.",
    },
    {
        key: 'internationalPricePctOfUS',
        label: 'International median price as % of US (brand proxy)',
        group: 'Drugs (Sec 405) – empirical anchor',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'OECD-style comparative pricing', evidence: ['Typical empirical finding: US net prices often ~2–3× peer-country prices.'] },
        description: 'Aggregate anchor for IRP: international median price expressed as a fraction of US price.',
    },
    {
        key: 'domesticValuePricePctOfUS',
        label: 'Domestic Value Benchmark price as % of US (brand proxy)',
        group: 'Drugs (Sec 405) – value benchmark proxy',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Sec. 405(c) DVB + HTA/value pricing literature', evidence: ['Placeholder anchor for value-based ceiling; intended for stress testing.'] },
        description: 'Aggregate anchor for DVB: value-based price expressed as a fraction of US price.',
    },
    {
        key: 'enforceIRP',
        label: 'Enforce International Reference Price (IRP)',
        group: 'Drugs (Sec 405) – enforcement realism',
        unit: 'scalar_0_1',
        source: {
            kind: PARAM_SOURCE.MODEL,
            citation: 'Sec. 405(d) + Sec. 405(g)(2)',
            excerpt: '“... the ‘International Reference Price’ is the median ex-factory price, net of all standard rebates, for the drug in the ‘Reference Countries’.”\n\n“... If the application of the International Reference Price ... is held to be invalid ...”',
            evidence: ['Models practical enforceability (data availability, compliance, carve-outs) separate from the statutory formula.']
        },
        description: 'How strongly IRP is applied (0 none, 1 full).',
    },
    {
        key: 'enforceDrugCeilings',
        label: 'Enforce statutory drug price ceilings (DVB machinery)',
        group: 'Drugs (Sec 405) – enforcement realism',
        unit: 'scalar_0_1',
        source: {
            kind: PARAM_SOURCE.MODEL,
            citation: 'Sec. 405(b)(1)(A) + Sec. 405(c)',
            excerpt: '“... shall not exceed the lower of: (A) The Domestic Value Benchmark ...”\n\n“... the Secretary shall use established clinical-effectiveness data (such as Quality-Adjusted Life Years or similar metrics) ... and shall explicitly exclude costs related to direct-to-consumer advertising and lobbying.”',
            evidence: ['Models operational enforceability of the DVB/statutory ceiling machinery (capacity, rulemaking, adjudication, compliance).']
        },
        description: 'How strongly DVB/statutory ceilings are applied (0 none, 1 full).',
    },

    // ===========================
    // Provider behavior (Model)
    // ===========================
    {
        key: 'providerParticipationRate',
        label: 'Provider participation rate',
        group: 'Providers (behavioral)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Behavioral sensitivity', evidence: ['Lower participation implies more balance billing / access friction.'] },
        description: 'Fraction of providers participating at SAFECARE rates.',
    },
    {
        key: 'utilizationSurge',
        label: 'Utilization surge for newly insured',
        group: 'Utilization (behavioral)',
        unit: 'percent',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'RAND/HIE directionality (calibration pending)', evidence: ['Short-run demand rises when prices fall and coverage expands; magnitude is uncertain.'] },
        description: 'Short-run utilization increase in early implementation years.',
    },

    // ===========================
    // Compounding conservative discount
    // ===========================
    {
        key: '__CONSERVATIVE_FACTOR__',
        label: 'Conservative factor (discount applied to favorable compounding)',
        group: 'Compounding Effects',
        unit: 'scalar_0_1',
        source: { kind: PARAM_SOURCE.MODEL, citation: 'Model credibility safeguard', evidence: ['Discounts theoretical gains to reflect friction, delays, and imperfect execution.'] },
        description: 'Multiplier applied to favorable compounding effects to avoid over-claiming.',
        derived: true
    }
];

const REGISTRY_MAP = new Map(PARAMETER_REGISTRY.map(p => [p.key, p]));

export function getParamMeta(key) {
    return REGISTRY_MAP.get(key) || null;
}

export function formatValueByUnit(unit, value) {
    if (value === null || value === undefined) return '-';
    if (unit === 'percent') return `${(Number(value) * 100).toFixed(2)}%`;
    if (unit === 'scalar_0_1') return `${(clamp01(Number(value)) * 100).toFixed(1)}%`;
    if (unit === 'usd') return `$${Number(value).toLocaleString()}`;
    if (unit === 'years') return `${Number(value)} years`;
    if (unit === 'boolean') return value ? 'true' : 'false';
    if (unit === 'enum') return String(value);
    return Array.isArray(value) || isPlainObject(value) ? JSON.stringify(value) : String(value);
}

export function getDefaultValueForKey(key) {
    if (key === '__CONSERVATIVE_FACTOR__') return CONSERVATIVE_FACTOR;
    return defaultSafecareConfig[key];
}

export function listPolicyParameters(config, { onlyChanged = false } = {}) {
    const keys = Object.keys(config || {});
    keys.sort();
    return keys
        .filter(k => {
            if (!onlyChanged) return true;
            return !deepEqual(config[k], defaultSafecareConfig[k]);
        })
        .map(k => {
            const meta = getParamMeta(k);
            const defaultValue = defaultSafecareConfig[k];
            return {
                key: k,
                label: meta?.label || k,
                group: meta?.group || 'Unregistered / internal',
                unit: meta?.unit || 'raw',
                source: meta?.source || { kind: 'unregistered' },
                description: meta?.description || 'No registry entry yet.',
                value: config[k],
                defaultValue
            };
        });
}

export function listCompoundingParameters() {
    // Expose the conservative factor and "enabled" flags for each effect.
    const entries = [
        {
            key: '__CONSERVATIVE_FACTOR__',
            label: getParamMeta('__CONSERVATIVE_FACTOR__')?.label || 'Conservative factor',
            group: 'Compounding Effects',
            unit: 'scalar_0_1',
            source: getParamMeta('__CONSERVATIVE_FACTOR__')?.source || { kind: PARAM_SOURCE.MODEL },
            description: getParamMeta('__CONSERVATIVE_FACTOR__')?.description || '',
            value: CONSERVATIVE_FACTOR,
            defaultValue: CONSERVATIVE_FACTOR
        }
    ];

    const effectKeys = Object.keys(compoundingEffectsConfig).filter(k => {
        const v = compoundingEffectsConfig[k];
        return isPlainObject(v) && Object.prototype.hasOwnProperty.call(v, 'enabled');
    });
    effectKeys.sort();

    effectKeys.forEach(k => {
        entries.push({
            key: `compounding.${k}.enabled`,
            label: `${k} enabled`,
            group: 'Compounding Effects',
            unit: 'boolean',
            source: { kind: PARAM_SOURCE.MODEL, citation: 'Model structure' },
            description: compoundingEffectsConfig[k]?.economicMechanism || compoundingEffectsConfig[k]?.description || '',
            value: !!compoundingEffectsConfig[k].enabled,
            defaultValue: !!compoundingEffectsConfig[k].enabled
        });
    });

    return entries;
}

export function computeDerivedTelemetry({ config, year }) {
    if (!config || !year) return [];
    const yearsActive = year - (config.startYear || defaultSafecareConfig.startYear);

    const adminRate = getEffectiveAdminOverheadRate({
        yearsActive,
        enforceAdminSimplification: config.enforceAdminSimplification,
        adminEfficiencyFloor: config.adminEfficiencyFloor
    });

    const drugInputs = getDrugCeilingInputs(config);
    const drugEffective = getEffectiveDrugCeilingPricePctOfUS(config);

    return [
        {
            key: '__derived.adminOverheadRate__',
            label: 'Effective administrative overhead rate (this year)',
            group: 'Derived (Administration)',
            unit: 'percent',
            source: { kind: PARAM_SOURCE.DERIVED, citation: 'Derived from schedule + enforcement + floor' },
            description: 'Admin overhead rate applied to the medical services base for the selected year.',
            value: adminRate
        },
        {
            key: '__derived.drugCeilingPricePctOfUS__',
            label: 'Effective brand price ceiling (% of US) under selected regime',
            group: 'Derived (Drugs)',
            unit: 'percent',
            source: { kind: PARAM_SOURCE.DERIVED, citation: 'Sec. 405 min(IRP,DVB) + enforcement realism' },
            description: 'Effective ceiling after applying regime selection and enforcement intensity.',
            value: drugEffective.effectivePricePctOfUS
        },
        {
            key: '__derived.drugCeilingIRPPricePctOfUS__',
            label: 'IRP anchor price (% of US) before enforcement',
            group: 'Derived (Drugs)',
            unit: 'percent',
            source: { kind: PARAM_SOURCE.DERIVED, citation: 'Inputs: internationalPricePctOfUS × 110%' },
            description: 'IRP anchor computed from international price proxy and statutory IRP multiplier.',
            value: drugInputs.irpPricePctOfUS
        },
        {
            key: '__derived.drugCeilingDVBPricePctOfUS__',
            label: 'DVB anchor price (% of US) before enforcement',
            group: 'Derived (Drugs)',
            unit: 'percent',
            source: { kind: PARAM_SOURCE.DERIVED, citation: 'Input: domesticValuePricePctOfUS' },
            description: 'DVB anchor price proxy before enforcement adjustments.',
            value: drugInputs.dvbPricePctOfUS
        }
    ];
}

